<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import {
		ActionsContainer,
		AdvancedCard,
		Alert,
		Button,
		CardGrid,
		Container,
		DeviceButton,
		IconButton,
		Input,
		ListNumberCard,
		Modal,
		Text,
		Loader,
	} from '@Shelly/shelly-design-system';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { getTemplateComponents, getTemplateService } from 'src/helpers/xt1_helpers/template_helpers';
	import { sortOn } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceStatus,
	} from 'src/stores/device-runtime/status-selectors';
	import { isSequenceRunning } from 'src/helpers/xt1_helpers/irrigation/irrigation_helpers';
	import IrrigationConfirmModal from './IrrigationConfirmModal.svelte';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Sequence, TemplateComponent, TemplateService } from 'src/types/VirtualComponents';
	import type { Device } from 'src/types/Device';

	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;

	$: statusStore = getDeviceStatus(device.id);
	$: settingsStore = getDeviceSettings(device.id);
	$: status = $statusStore;
	$: settings = $settingsStore;

	let waitForResponse = false;
	let waitForSaveResponse = false;
	let waitForDeleteResponse = false;
	let waitForRunResponse = {};
	let showEditModal = false;
	let deleteSequencePrompt = false;
	let responseError = '';
	let selectedSequence: Sequence = null;
	let sequences: Sequence[] = null;
	let confirmModal: IrrigationConfirmModal;

	const getListSequences = async () => {
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Service.ListSequences', { id: 0 });
		if (res?.isok) {
			//@ts-ignore
			sequences = res?.sequences || res?.data?.sequences || [];
		} else {
			responseError = $format('alert_danger_options_list_error', {
				default: 'Could not load the options list.',
			});
		}
		waitForResponse = false;
	};

	const templateService: TemplateService = getTemplateService(device);
	const sequencesMaxCount = templateService?.settings?._info?.meta?.service?.limit_sequences || 5;
	const sequenceName = 'New Sequence';
	const zones: TemplateComponent[] = sortOn(getTemplateComponents(device, null, /^zone\d+/), 'role');
	const saveZoneSetting = async () => {
		if (confirmModal && !(await confirmModal.confirm())) return;
		waitForSaveResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Service.SetSequence', {
			id: 0,
			...selectedSequence,
		});
		if (res?.isok) {
			showEditModal = false;
			getListSequences();
		}
		onSettingsResponse(res, device);
		waitForSaveResponse = false;
	};

	const flipDurationMs = 0;
	let dragDisabled = true;
	let selectedZoneDnd: {
		id: number;
		name: string;
		isDndShadowItem?: boolean;
	}[] = [];
	let notSelectedZoneDnd: {
		id: number;
		name: string;
		isDndShadowItem?: boolean;
	}[] = [];

	const dndHandleClick = (e: MouseEvent | TouchEvent) => {
		if (e.cancelable) e.preventDefault();
		const button = e.currentTarget as HTMLElement;
		button.classList.toggle('dnd-handle-drag');
	};

	const handleStartDrag = (e: MouseEvent | TouchEvent) => {
		dndHandleClick(e);
		dragDisabled = false;
	};

	const handleEndDrag = (e: TouchEvent | MouseEvent) => {
		dndHandleClick(e);
		dragDisabled = true;
	};

	const handleSelectedReorder = (e: CustomEvent) => {
		selectedZoneDnd = e.detail.items;
		reorderZonesOnDrag();
		dragDisabled = true;
	};

	const handleNotSelectedReorder = (e: CustomEvent) => {
		notSelectedZoneDnd = e.detail.items;
		dragDisabled = true;
	};

	const transformDraggedElement = (draggedEl: HTMLElement) => {
		// Visual only — svelte-dnd-action owns focus / keyboard a11y.
		const child = draggedEl.firstElementChild as HTMLElement | null;
		if (!child) return;
		child.setAttribute('tabIndex', '-1');
		child.classList.add('on-drag');
	};

	const onAddZone = (zoneID: number) => {
		selectedSequence.zones.push(zoneID);
		reorderZones();
	};

	const onRemoveZone = (zoneID: number) => {
		selectedSequence.zones = selectedSequence.zones.filter((i) => i != zoneID);
		reorderZones();
	};

	const reorderZones = () => {
		selectedZoneDnd = [];
		notSelectedZoneDnd = [];
		for (let i = 0; i < selectedSequence.zones.length; ++i) {
			const id = selectedSequence.zones[i];
			selectedZoneDnd.push({
				id,
				name: templateService?.settings?.zones?.[id]?.name || zones[id].config.name,
				isDndShadowItem: false,
			});
		}
		for (let i = 0; i < zones.length; ++i) {
			if (!selectedSequence.zones.includes(i)) {
				notSelectedZoneDnd.push({
					id: i,
					name: templateService?.settings?.zones?.[i]?.name || zones[i].config.name,
					isDndShadowItem: false,
				});
			}
		}
	};

	const reorderZonesOnDrag = () => {
		selectedSequence.zones = [];
		selectedZoneDnd.forEach((i) => {
			selectedSequence.zones.push(i.id);
		});
		selectedSequence = selectedSequence;
	};

	const deleteSequence = async () => {
		if (waitForDeleteResponse) return;
		waitForDeleteResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Service.RemoveSequence', {
			id: 0,
			seq_id: selectedSequence.seq_id,
		});
		if (res?.isok) {
			deleteSequencePrompt = false;
			getListSequences();
		}
		onSettingsResponse(res, device);
		waitForDeleteResponse = false;
	};

	const runSequence = async (index: number) => {
		if (waitForRunResponse[index]) return;
		waitForRunResponse[index] = true;
		const option = settings?.['enum:200']?.options?.[index + 1] || 'none';
		const deviceStatus = readDeviceStatus(device.id);
		const res: SettingRequest = await gen2Request(device, 'enum.set', {
			id: 200,
			value: deviceStatus?.['enum:200']?.value == option ? 'none' : option,
		});
		if (!res?.isok) onSettingsResponse(res, device);
		waitForRunResponse[index] = false;
	};

	const openAddSequenceModal = () => {
		selectedSequence = { name: sequenceName, zones: [] };
		reorderZones();
		showEditModal = true;
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && !waitForResponse && !responseError && sequences !== null) {
			stickyBottomAction.set({
				onClick: openAddSequenceModal,
				loading: waitForSaveResponse,
				disabled: sequences.length >= sequencesMaxCount,
				label: $format('text_irrigation_add_new_sequence', { default: 'Add new sequence' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	getListSequences();
</script>

{#if waitForResponse}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else if responseError}
	<Alert class="mt-m mb-s" type="danger" heading={responseError} />
	<Button icon="fa-solid fa-arrows-rotate" size="m" class="width-full" on:click={getListSequences}>
		{$format('button_rescan', { default: 'Rescan' })}
	</Button>
{:else if sequences}
	{#each sequences as sequence, i}
		<AdvancedCard
			title={sequence?.name || sequenceName}
			class="mt-s"
			avatar={{ icon: 'far fa-droplet' }}
		>
			<div slot="right" class="d-flex">
				<DeviceButton
					id="devicebutton-a9f590c"
					aria-label={status?.['enum:200']?.value ==
					settings?.['enum:200']?.options?.[i + 1]
						? $format('label_pause_sequence', { default: 'Pause Sequence' })
						: $format('label_run_sequence', { default: 'Run Sequence' })}
					class="align-self-center"
					style="margin-right:6px"
					icon={`fas fa-${status?.['enum:200']?.value == settings?.['enum:200']?.options?.[i + 1] ? 'pause' : 'play'}`}
					size="m"
					variant="primary"
					isActive={status?.['enum:200']?.value ==
						settings?.['enum:200']?.options?.[i + 1]}
					isLoading={waitForRunResponse[i]}
					on:click={() => runSequence(i)}
				/>

				<ActionsContainer orientation="vertical" type="simple">
					<IconButton
						aria-label={$format('button_edit', { default: 'Edit' })}
						icon="fas fa-pen"
						slot="first"
						variant="primary"
						on:click={() => {
							selectedSequence = sequences[i];
							reorderZones();
							showEditModal = true;
						}}
					/>
					<IconButton
						aria-label={$format('button_delete', { default: 'Delete' })}
						icon="fas fa-trash"
						variant="danger"
						slot="second"
						on:click={() => {
							selectedSequence = sequences[i];
							deleteSequencePrompt = true;
						}}
					/>
				</ActionsContainer>
			</div>
		</AdvancedCard>
	{/each}

	{#if !$showNewLayout}
		<Button
			class="width-full mt-s mb-s"
			size="m"
			disabled={sequences.length >= sequencesMaxCount}
			loading={waitForSaveResponse}
			on:click={openAddSequenceModal}
			>{$format('text_irrigation_add_new_sequence', {
				default: 'Add new sequence',
			})}</Button
		>
	{/if}
{/if}

{#if showEditModal}
	<Modal
		showModal={showEditModal}
		on:close={() => (showEditModal = false)}
		title={selectedSequence.name}
		removeScrollPadding
	>
		<svelte:fragment slot="content">
			<Container padding="scrollPaddings">
				<Text element="h2" styleType="h2" class="mb-s">
					{$format('label_name', { default: 'Name' })}
				</Text>
				<Input
					aria-labelledby="input-nwfwyu1"
					class="mb-m"
					size="m"
					icon="fas fa-edit"
					label={$format('text_irrigation_controller_zone_label', {
						default: 'Zone',
					})}
					value={selectedSequence.name}
					on:input={(e) => {
						selectedSequence.name = e.target['value'];
					}}
					id="input-nwfwyu1"
				/>
				<Text element="h2" class="mb-s">
					{$format('text_irrigation_selected_zones', {
						default: 'Selected irrigation zones',
					})}
				</Text>

				<CardGrid
					dndSettings={{
						items: selectedZoneDnd,
						flipDurationMs: flipDurationMs,
						dragDisabled: dragDisabled,
						transformDraggedElement,
						type: 'params',
					}}
					on:consider={handleSelectedReorder}
					on:finalize={handleSelectedReorder}
					widthUnit="%"
					sm={100}
					gridGap="s"
					gridRowMinContent
					gridStyledDndZone
					gridStyledDndZoneText={$format('drag_and_drop_your_irrigation_zone_here', {
						default: 'Drop your irrigation zone here.',
					})}
				>
					{#if selectedZoneDnd.length === 0}
						<Alert
							heading={$format('drag_and_drop_irrigation_zone_empty', {
								default: 'The list of irrigation zones is empty.',
							})}
						/>
					{/if}
					{#each selectedZoneDnd as content, i (content.id)}
						<div class:dnd-placeholder={content.isDndShadowItem}>
							<ListNumberCard
								editMode
								size="s"
								cardNumber={i + 1 >= 10 ? i + 1 : `0${i + 1}`}
							>
								<IconButton
									aria-label={$format('button_drag', {
										default: 'Drag and drop',
									})}
									on:mousedown={handleStartDrag}
									on:touchstart={handleStartDrag}
									on:mouseup={handleEndDrag}
									on:touchend={handleEndDrag}
									icon="fas fa-up-down-left-right"
									dndHandle
									class="mr-s"
								/>
								<Text class="width-full" noWrap styleType="body-small">
									{content.name}
								</Text>
								<IconButton
									aria-label={$format('button_remove', { default: 'Remove' })}
									class="ml-s"
									icon="fas fa-minus"
									variant="danger"
									on:click={() => onRemoveZone(content.id)}
								/>
							</ListNumberCard>
						</div>
					{/each}
				</CardGrid>

				<Text element="h2" class="mb-s mt-l">
					{$format('drag_and_drop_available_irrigation_zone_select', {
						default: 'Available irrigation zones',
					})}
				</Text>
				<CardGrid
					dndSettings={{
						items: notSelectedZoneDnd,
						flipDurationMs: flipDurationMs,
						dragDisabled: dragDisabled,
						transformDraggedElement,
						type: 'params',
					}}
					on:consider={handleNotSelectedReorder}
					on:finalize={handleNotSelectedReorder}
					widthUnit="%"
					sm={100}
					gridGap="s"
					title="Test title"
					gridRowMinContent
				>
					{#if notSelectedZoneDnd.length === 0}
						<Alert
							heading={$format(
								'alert_heading_drag_and_drop_available_irrigation_zone_empty',
								{
									default:
										'The list of available irrigation zones is empty since all options are currently selected.',
								},
							)}
						/>
					{/if}
					{#each notSelectedZoneDnd as content, i (content.id)}
						<div class:dnd-placeholder={content.isDndShadowItem}>
							<ListNumberCard editMode size="s">
								<IconButton
									aria-label={$format('button_drag', {
										default: 'Drag and drop',
									})}
									on:mousedown={handleStartDrag}
									on:touchstart={handleStartDrag}
									on:mouseup={handleEndDrag}
									on:touchend={handleEndDrag}
									icon="fas fa-up-down-left-right"
									dndHandle
									class="mr-s"
								/>
								<Text class="width-full" noWrap styleType="body-small">
									{content.name}
								</Text>
								<IconButton
									aria-label={$format('button_add', { default: 'Add' })}
									class="ml-s"
									icon="fas fa-plus"
									variant="success"
									on:click={() => onAddZone(content.id)}
								/>
							</ListNumberCard>
						</div>
					{/each}
				</CardGrid>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (showEditModal = false)}
				>{$format('button_cancel', { default: 'Cancel' })}</Button
			>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{
					sm: 'm',
				}}
				disabled={waitForSaveResponse}
				loading={waitForSaveResponse}
				on:click={saveZoneSetting}>{$format('button_save', { default: 'Save' })}</Button
			>
		</div>
	</Modal>
{/if}

{#if deleteSequencePrompt}
	<Modal
		showModal={deleteSequencePrompt}
		backdropClose={false}
		on:close={() => {
			deleteSequencePrompt = false;
		}}
		title={$format('button_delete', { default: 'Delete' })}
	>
		<Container slot="content" padding="0">
			<Text>
				{$format('text_script_on_delete_msg', {
					values: { this_type: selectedSequence?.name || sequenceName },
					default: 'Are you sure you want to delete {this_type}?',
				})}
			</Text>
		</Container>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (deleteSequencePrompt = false)}
				>{$format('button_cancel', { default: 'Cancel' })}</Button
			>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				loading={waitForDeleteResponse}
				responsiveSize={{
					sm: 'm',
				}}
				on:click={deleteSequence}
			>
				{$format('button_delete', { default: 'Delete' })}
			</Button>
		</div>
	</Modal>
{/if}

{#if isSequenceRunning(device)}
	<IrrigationConfirmModal bind:this={confirmModal} />
{/if}
